/**
 * @file Отмена асинхронного ожидания и задержек.
 *
 * AbortSignal — общий сигнал пользователя/тайм-аута. withAbort прекращает
 * ожидание Promise, но саму операцию должен остановить её адаптер.
 */
export function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
    signal.throwIfAborted()

    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', abort)
            resolve()
        }, ms)

        function abort() {
            clearTimeout(timer)
            reject(signal.reason)
        }

        signal.addEventListener('abort', abort, { once: true })
    })
}

// Обрабатываем исходный Promise даже после отмены ожидания, чтобы поздняя ошибка
// не стала необработанным отклонением (unhandled rejection).
export function withAbort<T>(
    promise: Promise<T>,
    signal: AbortSignal,
): Promise<T> {
    if (signal.aborted) {
        void promise.catch(() => {})

        return Promise.reject(signal.reason)
    }

    // Создаём второй Promise: он заканчивается либо вместе с исходным,
    // либо раньше по сигналу отмены. Сам исходный Promise продолжает существовать.
    return new Promise((resolve, reject) => {
        const abort = () => reject(signal.reason)

        signal.addEventListener('abort', abort, { once: true })

        // Оба обработчика нужны даже после отмены внешнего ожидания:
        // поздний reject исходной операции должен оставаться обработанным.
        promise.then(
            (value) => {
                signal.removeEventListener('abort', abort)
                resolve(value)
            },
            (error) => {
                signal.removeEventListener('abort', abort)
                reject(error)
            },
        )
    })
}
